import React, { useCallback, useEffect, useMemo } from 'react';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult
} from '@hello-pangea/dnd';
import CompanyItemToggle from './CompanyItemToggle';
import { useApiPut, useInvalidateQuery } from '../hooks/useApi';
import { ItemConfig } from '../models/Company';

type Props = {
  items: ItemConfig[];
  companyId: string | number;
  onSave: () => void;
};

const reorder = (list: ItemConfig[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const PriorityOrderList: React.FC<Props> = ({ items, companyId, onSave }) => {
  const [itemOrdered, setItemOrdered] = React.useState<ItemConfig[]>([]);
  const { mutateAsync, isPending } = useApiPut<any, any>(
    `/items-priority-list/${companyId}`,
    undefined,
    { skipInvalidation: true }
  );
  const { invalidate } = useInvalidateQuery(
    `/items-priority-list/${companyId}`
  );

  const onDragEnd = useCallback(
    async (result: DropResult) => {
      if (!result.destination) return;
      const newList = reorder(
        itemOrdered,
        result.source.index,
        result.destination.index
      );
      const lastOrder = [...itemOrdered];
      // send new order (array of ids) to backend
      // send new order to backend by updating each item with its new index
      try {
        setItemOrdered(newList);
        await Promise.all(
          newList.map((it, idx) => mutateAsync({ order: idx, ...it } as any))
        );
        await invalidate();
        onSave();
      } catch (e) {
        console.error('reorder failed', e);
        setItemOrdered(lastOrder);
      }
    },
    [itemOrdered, mutateAsync, onSave, invalidate]
  );

  const droppableId = useMemo(() => `priority-${companyId}`, [companyId]);

  useEffect(() => {
    setItemOrdered(
      items.slice().sort((a, b) => (a.order || 0) - (b.order || 0))
    );
  }, [items]);

  return (
    <div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={droppableId} disabled={isPending}>
          {(provided: any) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {itemOrdered.map((it, index) => (
                <Draggable
                  key={String(it.id)}
                  draggableId={String(it.id)}
                  index={index}
                >
                  {(prov: any) => (
                    <div
                      ref={prov.innerRef}
                      {...prov.draggableProps}
                      {...prov.dragHandleProps}
                      className="mb-2 cursor-move"
                    >
                      <CompanyItemToggle
                        {...it}
                        id={it.id}
                        urlToggle={`/items-priority-list/${companyId}`}
                        onSave={onSave}
                        disabled={isPending}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default PriorityOrderList;
